import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Upload, Waves, Timer, Zap, Heart, TrendingUp, Scissors, Rocket, Send, Check } from 'lucide-react';

/**
 * HeroPipelinePremium — 10-15s scripted loop of the real product pipeline.
 *
 * Each STAGE below is a discrete step the user sees on the landing hero. The
 * component sequences them via a single interval so the whole loop stays in
 * sync. Nothing here is a video — it's DOM+SVG so it stays 60fps on any device.
 *
 * Design honesty: every UI atom (waveform, radar-ish score bars, timeline marks,
 * clip stack, publish button) mirrors what the real app displays.
 */

const ACC = '#D4FF00';

// Ordered story of what happens after upload.
const STAGES = [
  { id: 'upload',    icon: Upload,      label: 'Upload',            sub: '58:24 · podcast.mp4',    color: ACC },
  { id: 'audio',     icon: Waves,       label: 'Analyzing audio',   sub: '44.1 kHz · whisper',      color: '#00E5FF' },
  { id: 'timeline',  icon: Timer,       label: 'Timeline lit up',   sub: '12 hooks detected',       color: '#00E5FF' },
  { id: 'hook',      icon: Zap,         label: 'Hook Score',        sub: '94/100',                  color: '#F59E0B' },
  { id: 'emotion',   icon: Heart,       label: 'Emotion Score',     sub: '88/100',                  color: '#A855F7' },
  { id: 'viral',     icon: TrendingUp,  label: 'Viral Score',       sub: '96/100',                  color: ACC },
  { id: 'clips',     icon: Scissors,    label: '5 clips generated', sub: 'auto-reframe 9:16',       color: ACC },
  { id: 'ready',     icon: Rocket,      label: 'TikTok Ready',      sub: 'captions burned in',      color: '#FF3D7F' },
  { id: 'publish',   icon: Send,        label: 'Publish',           sub: 'TikTok · Reels · Shorts', color: '#22C55E' },
];

const STAGE_MS = 1400; // per stage — total loop ≈ 12.6s

const HeroPipelinePremium = () => {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (reduce) { setStep(STAGES.length - 1); return; }
    const id = setInterval(() => {
      setStep((s) => (s + 1) % STAGES.length);
    }, STAGE_MS);
    return () => clearInterval(id);
  }, [reduce]);

  const current = STAGES[step];

  return (
    <div
      className="relative w-full h-[520px] rounded-3xl border border-white/8 bg-gradient-to-br from-white/[0.03] to-transparent overflow-hidden"
      data-testid="hero-pipeline-premium"
    >
      {/* Ambient glow that colour-shifts with the active stage */}
      <motion.div
        className="absolute -top-16 left-1/2 -translate-x-1/2 w-[70%] h-64 rounded-full blur-3xl pointer-events-none"
        animate={{ backgroundColor: `${current.color}22` }}
        transition={{ duration: 0.6 }}
      />

      {/* Top-left: source card (persistent) */}
      <div className="absolute left-6 top-6 w-52 rounded-xl border border-white/10 bg-black/40 backdrop-blur-md p-3 z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Source</span>
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        </div>
        <div className="aspect-video rounded-md bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden relative">
          <SmallWaveform />
          <div className="absolute bottom-1.5 right-2 text-[9px] font-mono text-white/70 bg-black/60 px-1.5 rounded">58:24</div>
        </div>
      </div>

      {/* Center: the animated pipeline canvas */}
      <div className="absolute inset-0 flex items-center justify-center">
        <StageCanvas step={step} />
      </div>

      {/* Bottom: horizontal step tracker */}
      <div className="absolute left-0 right-0 bottom-0 p-5 z-10">
        <StageTracker step={step} />
      </div>

      {/* Live stage badge (top-right) */}
      <div className="absolute right-6 top-6 z-10" data-testid="hero-live-stage">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-2.5 px-3.5 py-2 rounded-full border backdrop-blur-md"
            style={{ borderColor: `${current.color}55`, backgroundColor: `${current.color}12` }}
          >
            <current.icon className="w-3.5 h-3.5" style={{ color: current.color }} />
            <div className="leading-tight">
              <div className="text-[10px] font-semibold" style={{ color: current.color }}>{current.label}</div>
              <div className="text-[9px] text-zinc-400 font-mono">{current.sub}</div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// -------- small waveform that lives inside the source thumbnail --------
const SmallWaveform = () => (
  <div className="absolute inset-0 flex items-center gap-[2px] px-2 py-2">
    {Array.from({ length: 22 }).map((_, i) => (
      <motion.div
        key={i}
        className="flex-1 rounded-sm bg-white/40"
        animate={{ height: ['20%', '85%', '35%', '70%', '25%'] }}
        transition={{ duration: 1.1 + (i % 3) * 0.15, repeat: Infinity, delay: i * 0.05 }}
      />
    ))}
  </div>
);

// -------- center pipeline visuals — one composition per step -----------
const StageCanvas = ({ step }) => {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* stages 0 & 1 & 2: timeline reveal */}
      <TimelineBoard active={step >= 1} lit={step >= 2} />
      {/* stages 3-5: score chips flying in on top of timeline */}
      <ScoreStack step={step} />
      {/* stages 6-8: clips stack + publish */}
      <ClipStack step={step} />
    </div>
  );
};

// Central rectangle timeline. Bars pulse; when `lit` a highlighted region appears.
const TimelineBoard = ({ active, lit }) => (
  <motion.div
    initial={false}
    animate={{ opacity: active ? 1 : 0.55, scale: active ? 1 : 0.97 }}
    transition={{ duration: 0.4 }}
    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[35%] w-[64%] max-w-md rounded-xl border border-white/10 bg-black/50 backdrop-blur-md p-4"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Timeline · Audio</span>
      <span className="text-[9px] font-mono" style={{ color: lit ? ACC : '#71717a' }}>
        {lit ? '12 hooks detected' : 'analyzing…'}
      </span>
    </div>
    <div className="relative h-16 rounded-md bg-black/60 overflow-hidden">
      <div className="absolute inset-0 flex items-end gap-[3px] p-1.5">
        {Array.from({ length: 42 }).map((_, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-sm"
            style={{ background: '#3f3f46' }}
            animate={{
              height: [`${20 + (i % 5) * 10}%`, `${45 + (i % 7) * 8}%`, `${25 + (i % 3) * 15}%`],
              backgroundColor: lit && (i === 10 || i === 24 || i === 33) ? ACC : '#3f3f46',
            }}
            transition={{ duration: 1.4 + (i % 4) * 0.12, repeat: Infinity, delay: i * 0.02 }}
          />
        ))}
      </div>
      {lit && (
        <>
          {[10, 24, 33].map((pos) => (
            <motion.div
              key={pos}
              initial={{ opacity: 0, scaleY: 0 }}
              animate={{ opacity: 1, scaleY: 1 }}
              transition={{ duration: 0.4 }}
              className="absolute top-0 bottom-0 w-[6%] border-x"
              style={{
                left: `${(pos / 42) * 100}%`,
                borderColor: ACC,
                background: `linear-gradient(180deg, ${ACC}25, ${ACC}05)`,
              }}
            />
          ))}
        </>
      )}
    </div>
  </motion.div>
);

const SCORE_ROWS = [
  { key: 'hook',    step: 3, label: 'Hook',    value: 94, color: '#F59E0B' },
  { key: 'emotion', step: 4, label: 'Emotion', value: 88, color: '#A855F7' },
  { key: 'viral',   step: 5, label: 'Viral',   value: 96, color: ACC },
];

const ScoreStack = ({ step }) => (
  <div className="absolute top-[52%] left-1/2 -translate-x-1/2 w-[64%] max-w-md space-y-1.5">
    {SCORE_ROWS.map((s) => (
      <AnimatePresence key={s.key}>
        {step >= s.step && (
          <motion.div
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="flex items-center gap-3 rounded-lg border px-3 py-2 backdrop-blur-md"
            style={{ borderColor: `${s.color}40`, backgroundColor: `${s.color}0d` }}
          >
            <span className="text-[10px] uppercase tracking-widest font-mono w-14" style={{ color: s.color }}>{s.label}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${s.value}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                style={{ backgroundColor: s.color }}
              />
            </div>
            <span className="text-xs font-mono tabular-nums font-bold" style={{ color: s.color }}>{s.value}</span>
          </motion.div>
        )}
      </AnimatePresence>
    ))}
  </div>
);

// Right side: stack of vertical clip tiles that appear + a publish confirm.
const CLIPS = [
  { title: 'The moment everything shifted', score: 96, dur: 42 },
  { title: 'Wait — did he just say that?',  score: 91, dur: 38 },
  { title: 'This changes how I see it',     score: 88, dur: 55 },
  { title: 'You NEED to hear this',         score: 84, dur: 27 },
  { title: 'The rule nobody talks about',   score: 82, dur: 44 },
];

const ClipStack = ({ step }) => {
  const visible = step >= 6;
  const publishing = step === 8;
  return (
    <div className="absolute right-6 top-1/2 -translate-y-1/2 w-40 flex flex-col items-end gap-2">
      {CLIPS.map((c, i) => (
        <motion.div
          key={i}
          initial={false}
          animate={{
            opacity: visible ? 1 : 0,
            y: visible ? 0 : 20,
            scale: visible ? 1 : 0.85,
          }}
          transition={{ duration: 0.35, delay: visible ? i * 0.08 : 0 }}
          className="relative w-36 aspect-[9/16] rounded-lg border border-white/10 overflow-hidden shadow-lg"
          style={{
            background: `linear-gradient(135deg, hsl(${i * 65}, 40%, 20%), #0a0a0a)`,
            transform: `translateX(${i * -6}px) rotate(${(i - 2) * 2}deg)`,
          }}
        >
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${ACC}20`, color: ACC, border: `1px solid ${ACC}66` }}>
            {c.score}
          </div>
          <div className="absolute bottom-1.5 right-1.5 text-[9px] font-mono text-white/70 bg-black/60 px-1 rounded">{c.dur}s</div>
          <div className="absolute inset-x-1.5 bottom-8">
            <div className="tiktok-caption text-[10px] leading-tight">{c.title.slice(0, 24).toUpperCase()}</div>
          </div>
        </motion.div>
      ))}
      {publishing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="absolute -bottom-6 right-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
          style={{ background: '#22C55E', color: '#000' }}
        >
          <Check className="w-3 h-3" /> Published
        </motion.div>
      )}
    </div>
  );
};

const StageTracker = ({ step }) => (
  <div className="flex items-center gap-1 rounded-full border border-white/8 bg-black/40 backdrop-blur-md px-3 py-2">
    {STAGES.map((s, i) => (
      <div key={s.id} className="flex items-center gap-1 flex-1">
        <motion.div
          className="h-1.5 flex-1 rounded-full"
          initial={false}
          animate={{ backgroundColor: i <= step ? s.color : '#27272a' }}
          transition={{ duration: 0.3 }}
        />
      </div>
    ))}
  </div>
);

export default HeroPipelinePremium;
